/**
 * useThreadViewDwell — "you have READ this thread" is a fact about attention,
 * not about a click (2026-09-08, PR-3; owner ask: the unread mark should
 * clear only after ~3 seconds of actually looking at the thread).
 *
 * The timer arms when a thread is active AND the tab is visible, runs for
 * THREAD_VIEW_DWELL_MS continuously, then fires `onDwell(threadId, tailKey)`
 * once for that (thread, tail) pair. Anything that means the viewer is not
 * looking any more disarms it: the tab hides, the thread switches, the hook
 * is disabled. A NEW tail (a message that just landed) re-arms it — the new
 * message has not been read yet, so the mark must not clear for it until it,
 * too, has been on screen for the dwell.
 *
 * Deliberately blind to focus: a visible tab beside another window is still
 * being looked at; `visibilityState` is the browser's word for "on screen".
 */
import { useEffect, useRef } from 'react';

export const THREAD_VIEW_DWELL_MS = 3_000;

export interface UseThreadViewDwellArgs {
  enabled: boolean;
  threadId: string | null | undefined;
  /**
   * Identity of the transcript tail (the newest non-system message id). A
   * change re-arms the timer; the value is handed back to `onDwell` so the
   * receiver knows WHAT was read.
   */
  tailKey: string | null | undefined;
  onDwell: (threadId: string, tailKey: string | null) => void;
  /** Test seam; production uses THREAD_VIEW_DWELL_MS. */
  dwellMs?: number;
}

function tabVisible(): boolean {
  if (typeof document === 'undefined') return true;
  return document.visibilityState !== 'hidden';
}

export function useThreadViewDwell(args: UseThreadViewDwellArgs): void {
  const { enabled, threadId, tailKey, dwellMs } = args;
  const onDwellRef = useRef(args.onDwell);
  onDwellRef.current = args.onDwell;
  const firedForRef = useRef<string | null>(null);

  useEffect(() => {
    const id = (threadId ?? '').trim();
    if (!enabled || !id) return;
    const pairKey = `${id}|${tailKey ?? ''}`;
    if (firedForRef.current === pairKey) return; // already read this tail
    const ms = typeof dwellMs === 'number' && dwellMs >= 0 ? dwellMs : THREAD_VIEW_DWELL_MS;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const disarm = (): void => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };
    const arm = (): void => {
      disarm();
      if (!tabVisible()) return;
      timer = setTimeout(() => {
        timer = null;
        firedForRef.current = pairKey;
        try {
          onDwellRef.current(id, tailKey ?? null);
        } catch {
          /* a failing receiver never breaks the view */
        }
      }, ms);
    };
    const onVisibility = (): void => {
      if (tabVisible()) arm();
      else disarm();
    };

    arm();
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
    return () => {
      disarm();
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, threadId, tailKey, dwellMs]);
}

/** The transcript tail's identity for the dwell: newest non-system message id. */
export function dwellTailKey(
  messages: ReadonlyArray<{ id?: string; isSystem?: boolean } | null | undefined> | null | undefined,
): string | null {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (!m || m.isSystem) continue;
    const id = typeof m.id === 'string' ? m.id.trim() : '';
    if (id) return id;
  }
  return null;
}
